import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, from } from 'rxjs';
import { map, exhaustMap, catchError, tap, switchMap } from 'rxjs/operators';
import { ApiService } from '../../shared/services/api.service';
import {
    getDataFlowsRequest,
    getDataFlowsSuccess,
    getDataFlowsFailure,
    getDataFlowByIdRequest,
    getDataFlowByIdSuccess,
    getDataFlowByIdFailure,
    updateDataFlowRequest,
    updateDataFlowSuccess,
    updateDataFlowFailure,
    addMessageRequest,
    addMessageSuccess,
    addMessageFailure,
    requestDocumentsRequest,
    requestDocumentsSuccess,
    requestDocumentsFailure,
    uploadVerifiedReportRequest,
    uploadVerifiedReportSuccess,
    uploadVerifiedReportFailure,
    submitLicenseSubmissionRequest,
    submitLicenseSubmissionSuccess,
    submitLicenseSubmissionFailure,
    uploadOfficialInvoiceRequest,
    uploadOfficialInvoiceSuccess,
    uploadOfficialInvoiceFailure,
    addExamBookingRequest,
    addExamBookingSuccess,
    addExamBookingFailure,
    updateExamBookingStatusRequest,
    updateExamBookingStatusSuccess,
    updateExamBookingStatusFailure,
    markDocumentAsUploadedRequest,
    markDocumentAsUploadedSuccess,
    markDocumentAsUploadedFailure,
} from '../actions/data-flow.actions';
import { GET_DATA_FLOWS, GET_CANDIDATE_DATA_FLOW, UPDATE_DATA_FLOW, ADD_DATA_FLOW_MESSAGE, REQUEST_DOCUMENT, UPLOAD_FILE, ADD_LICENSE_SUBMISSION, ADD_EXAM_BOOKING, UPDATE_EXAM_BOOKING_STATUS, MARK_REQUEST_DOCUMENT_AS_UPLOADED } from '../../utils/endpoints';
import { MyToastrService } from '../../shared/services/my-toastr.service';
import { CandidateDataFlowResponse } from '../../shared/models/data-flow-api.model';

@Injectable()
export class DataFlowEffects {
    private actions$ = inject(Actions);
    private apiService = inject(ApiService);
    private myToastrService = inject(MyToastrService);

    getDataFlowsRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getDataFlowsRequest),
            exhaustMap(({ filters }) => {
                const params: Record<string, string | number | boolean | undefined> = {
                    pageSize: filters.pageSize,
                    pageNumber: filters.pageNumber,
                };
                if (filters.candidateName?.trim()) params['candidateName'] = filters.candidateName.trim();
                if (filters.serviceType) params['serviceType'] = filters.serviceType;
                if (filters.progress) params['progress'] = filters.progress;
                if (filters.slaPriority != null) params['slaPriority'] = filters.slaPriority;
                if (filters.stage) params['stage'] = filters.stage;
                if (filters.status) params['status'] = filters.status;
                if (filters.isRequestdocumentUploaded != null) params['isRequestdocumentUploaded'] = filters.isRequestdocumentUploaded;
                return from(this.apiService.get<CandidateDataFlowResponse>(GET_DATA_FLOWS, params)).pipe(
                    map((response) => {
                        const body = response.data;
                        const data = body?.data ?? [];
                        const total = body?.total ?? 0;
                        return getDataFlowsSuccess({ data, total });
                    }),
                    catchError(error => of(getDataFlowsFailure({ message: error.response?.data?.message || 'Failed to load data flows' })))
                );
            })
        )
    );

    getDataFlowsFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getDataFlowsFailure),
            tap(({ message }) => {
                this.myToastrService.showError(message)
            })
        ),
        { dispatch: false }
    );

    getDataFlowByIdRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getDataFlowByIdRequest),
            exhaustMap(({ id }) =>
                from(this.apiService.get(`${GET_CANDIDATE_DATA_FLOW}/${id}`)).pipe(
                    map((response: any) => {
                        const data = response.data?.data || response.data;
                        return getDataFlowByIdSuccess({ data });
                    }),
                    catchError(error => of(getDataFlowByIdFailure({ message: error.response?.data?.message || 'Failed to load data flow' })))
                )
            )
        )
    );

    getDataFlowByIdFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getDataFlowByIdFailure),
            tap(({ message }) => {
                this.myToastrService.showError(message)
            })
        ),
        { dispatch: false }
    );

    updateDataFlowRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateDataFlowRequest),
            exhaustMap(({ id, payload }) =>
                from(this.apiService.post(`${UPDATE_DATA_FLOW}/${id}`, payload)).pipe(
                    map((response: any) => {
                        const data = response.data?.data || response.data;
                        this.myToastrService.showSuccess('Data flow updated successfully');
                        return updateDataFlowSuccess({ data });
                    }),
                    catchError(error => of(updateDataFlowFailure({ message: error.response?.data?.message || 'Failed to update data flow' })))
                )
            )
        )
    );

    updateDataFlowFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateDataFlowFailure),
            tap(({ message }) => {
                this.myToastrService.showError(message)
            })
        ),
        { dispatch: false }
    );

    addMessageRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(addMessageRequest),
            exhaustMap(({ id, message }) =>
                from(this.apiService.post(`${ADD_DATA_FLOW_MESSAGE}/${id}`, { message })).pipe(
                    map((response: any) => {
                        const data = response.data?.data || response.data;
                        this.myToastrService.showSuccess('Message sent successfully');
                        return addMessageSuccess({ data });
                    }),
                    catchError(error => of(addMessageFailure({ message: error.response?.data?.message || 'Failed to send message' })))
                )
            )
        )
    );

    addMessageFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(addMessageFailure),
            tap(({ message }) => {
                this.myToastrService.showError(message)
            })
        ),
        { dispatch: false }
    );

    requestDocumentsRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(requestDocumentsRequest),
            exhaustMap(({ id, documents }) =>
                from(this.apiService.post(`${REQUEST_DOCUMENT}/${id}`, documents)).pipe(
                    map((response: any) => {
                        const data = response.data?.data || response.data;
                        this.myToastrService.showSuccess('Document request sent successfully');
                        return requestDocumentsSuccess({ data });
                    }),
                    catchError(error => of(requestDocumentsFailure({ message: error.response?.data?.message || 'Failed to request documents' })))
                )
            )
        )
    );

    requestDocumentsFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(requestDocumentsFailure),
            tap(({ message }) => {
                this.myToastrService.showError(message)
            })
        ),
        { dispatch: false }
    );

    uploadVerifiedReportRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadVerifiedReportRequest),
            exhaustMap(({ id, file }) => {
                // Step 1: Upload file to /upload
                const formData = new FormData();
                formData.append('file', file);
                return from(this.apiService.post(UPLOAD_FILE, formData)).pipe(
                    switchMap((uploadResponse: any) => {
                        // Extract the file URL from upload response: response.data.data.url
                        const fileUrl = uploadResponse.data?.data?.url;

                        if (!fileUrl) {
                            throw new Error('File URL not found in upload response');
                        }

                        // Step 2: Update dataflow status with the uploaded file URL
                        return from(this.apiService.post(`${UPDATE_DATA_FLOW}/${id}`, {
                            status: 'dataflow_completed',
                            verifyDocument: fileUrl
                        })).pipe(
                            map((response: any) => {
                                const data = response.data?.data || response.data;
                                this.myToastrService.showSuccess('Dataflow Report Submitted Successfully');
                                return uploadVerifiedReportSuccess({ data });
                            })
                        );
                    }),
                    catchError(error => of(uploadVerifiedReportFailure({ message: error.response?.data?.message || 'Failed to upload verified report' })))
                );
            })
        )
    );

    uploadVerifiedReportFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadVerifiedReportFailure),
            tap(({ message }) => {
                this.myToastrService.showError(message)
            })
        ),
        { dispatch: false }
    );

    submitLicenseSubmissionRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(submitLicenseSubmissionRequest),
            exhaustMap(({ id, serviceFee, feeReason, invoiceFile, professionalLicenseFile, documents }) => {
                // Step 1: Upload invoice file and professional license file in parallel (if provided)
                const invoiceFormData = new FormData();
                if (invoiceFile) invoiceFormData.append('file', invoiceFile);
                const invoiceUpload = invoiceFile
                    ? this.apiService.post(UPLOAD_FILE, invoiceFormData)
                    : Promise.resolve({ data: { data: { url: null } } });

                const licenseFormData = new FormData();
                if (professionalLicenseFile) licenseFormData.append('file', professionalLicenseFile);
                const licenseUpload = professionalLicenseFile
                    ? this.apiService.post(UPLOAD_FILE, licenseFormData)
                    : Promise.resolve({ data: { data: { url: null } } });

                return from(Promise.all([invoiceUpload, licenseUpload])).pipe(
                    switchMap(([invoiceResponse, licenseResponse]: any[]) => {
                        const invoiceUrl = invoiceResponse.data?.data?.url;
                        const professionalLicenseUrl = licenseResponse.data?.data?.url;

                        // Step 2: Submit license submission (backend handles status update)
                        const payload: any = {
                            additionalFees: serviceFee,
                            additionalFeesReason: feeReason
                        };

                        if (invoiceUrl) {
                            payload.officialInvoiceURL = invoiceUrl;
                        }

                        return from(this.apiService.patch(`${ADD_LICENSE_SUBMISSION}/${id}`, payload)).pipe(
                            switchMap((licenseResponse: any) => {
                                // Step 3: Request documents if any (optional)
                                const validDocuments = documents.filter(
                                    doc => doc.documentName.trim() !== '' || doc.instructions.trim() !== ''
                                );

                                let documentsPromise: Promise<any>;
                                if (validDocuments.length > 0) {
                                    documentsPromise = this.apiService.post(`${REQUEST_DOCUMENT}/${id}`, validDocuments);
                                } else {
                                    documentsPromise = Promise.resolve({ data: {} });
                                }

                                return from(documentsPromise).pipe(
                                    switchMap(() => {
                                        // Step 4: Update status — journey_complete if professional license was uploaded, otherwise licensing_in_progress
                                        const statusPayload: any = {
                                            status: professionalLicenseUrl ? 'journey_complete' : 'licensing_in_progress'
                                        };
                                        if (professionalLicenseUrl) {
                                            statusPayload.ProfessionalLicense = professionalLicenseUrl;
                                        }
                                        return from(this.apiService.post(`${UPDATE_DATA_FLOW}/${id}`, statusPayload)).pipe(
                                            map((statusResponse: any) => {
                                                const updatedData = statusResponse.data?.data || statusResponse.data;
                                                this.myToastrService.showSuccess('License submission completed successfully');
                                                return submitLicenseSubmissionSuccess({ data: updatedData });
                                            })
                                        );
                                    })
                                );
                            }),
                            catchError(error => of(submitLicenseSubmissionFailure({ message: error.response?.data?.message || 'Failed to submit license submission' })))
                        );
                    }),
                    catchError(error => of(submitLicenseSubmissionFailure({ message: error.response?.data?.message || 'Failed to upload files' })))
                );
            })
        )
    );

    submitLicenseSubmissionFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(submitLicenseSubmissionFailure),
            tap(({ message }) => {
                this.myToastrService.showError(message)
            })
        ),
        { dispatch: false }
    );

    uploadOfficialInvoiceRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadOfficialInvoiceRequest),
            exhaustMap(({ id, file }) => {
                const formData = new FormData();
                formData.append('file', file);

                return from(this.apiService.post(UPLOAD_FILE, formData)).pipe(
                    switchMap((uploadResponse: any) => {
                        const fileUrl = uploadResponse.data?.data?.url;
                        if (!fileUrl) {
                            throw new Error('File URL not found in upload response');
                        }
                        return from(this.apiService.patch(`${ADD_LICENSE_SUBMISSION}/${id}`, { officialInvoiceURL: fileUrl })).pipe(
                            map((response: any) => {
                                const data = response.data?.data || response.data;
                                this.myToastrService.showSuccess('Official invoice uploaded successfully');
                                return uploadOfficialInvoiceSuccess({ data });
                            })
                        );
                    }),
                    catchError((error) =>
                        of(
                            uploadOfficialInvoiceFailure({
                                message: error.response?.data?.message || 'Failed to upload official invoice',
                            })
                        )
                    )
                );
            })
        )
    );

    uploadOfficialInvoiceFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(uploadOfficialInvoiceFailure),
                tap(({ message }) => {
                    this.myToastrService.showError(message);
                })
            ),
        { dispatch: false }
    );

    addExamBookingRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(addExamBookingRequest),
            exhaustMap(({ id, examCenterName, location, examFee, timeZone, slots, documents }) => {
                const payload = {
                    examFee,
                    location,
                    examCenterName,
                    timeZone,
                    slots
                };

                return from(this.apiService.patch(`${ADD_EXAM_BOOKING}/${id}`, payload)).pipe(
                    switchMap((response: any) => {
                        const data = response.data?.data || response.data;

                        // If there are documents to request, send them
                        const validDocuments = documents.filter(
                            doc => doc.documentName.trim() !== '' || doc.instructions.trim() !== ''
                        );

                        if (validDocuments.length > 0) {
                            return from(this.apiService.post(`${REQUEST_DOCUMENT}/${id}`, validDocuments)).pipe(
                                map(() => {
                                    this.myToastrService.showSuccess('Exam booking proposal sent successfully');
                                    return addExamBookingSuccess({ data });
                                })
                            );
                        }

                        this.myToastrService.showSuccess('Exam booking proposal sent successfully');
                        return of(addExamBookingSuccess({ data }));
                    }),
                    catchError(error => of(addExamBookingFailure({ message: error.response?.data?.message || 'Failed to submit exam booking' })))
                );
            })
        )
    );

    addExamBookingFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(addExamBookingFailure),
            tap(({ message }) => {
                this.myToastrService.showError(message)
            })
        ),
        { dispatch: false }
    );

    updateExamBookingStatusRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateExamBookingStatusRequest),
            exhaustMap(({ dataFlowId, bookingId, status }) => {
                const payload = {
                    dataFlowId,
                    bookingId,
                    status
                };

                return from(this.apiService.patch(UPDATE_EXAM_BOOKING_STATUS, payload)).pipe(
                    map((response: any) => {
                        const data = response.data?.data || response.data;
                        this.myToastrService.showSuccess('Exam result updated successfully');
                        return updateExamBookingStatusSuccess({ data });
                    }),
                    catchError(error => of(updateExamBookingStatusFailure({ message: error.response?.data?.message || 'Failed to update exam result' })))
                );
            })
        )
    );

    updateExamBookingStatusFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateExamBookingStatusFailure),
            tap(({ message }) => {
                this.myToastrService.showError(message)
            })
        ),
        { dispatch: false }
    );

    markDocumentAsUploadedRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(markDocumentAsUploadedRequest),
            exhaustMap(({ id, filters }) =>
                from(this.apiService.patch(`${MARK_REQUEST_DOCUMENT_AS_UPLOADED}/${id}`, {})).pipe(
                    switchMap((response: any) => {
                        const data = response.data?.data || response.data;
                        this.myToastrService.showSuccess('Marked as reviewed successfully');
                        return of(markDocumentAsUploadedSuccess({ data }), getDataFlowsRequest({ filters }));
                    }),
                    catchError(error => of(markDocumentAsUploadedFailure({ message: error.response?.data?.message || 'Failed to mark as reviewed' })))
                )
            )
        )
    );

    markDocumentAsUploadedFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(markDocumentAsUploadedFailure),
            tap(({ message }) => {
                this.myToastrService.showError(message)
            })
        ),
        { dispatch: false }
    );
}
